import * as React from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PostPurchaseService from 'services/PostPurchaseService';
import { pacific, pct, usd } from './shared';
import { BREAKDOWN_DIMENSIONS, BreakdownDimension, BreakdownResponse, DesignStats, Period, Taker, Upsell } from './types';
import { PeriodPicker } from './PeriodPicker';
import { sumBreakdown } from './breakdown';

// The period of the stats on screen (the API's own from/to), not the one being picked.
type ShownPeriod = { from: string; to: string };

// Performance by design: one row per (upsell, design), or broken down further by any of
// date, landing page, cart and matched rule ("Break down by"), over the table's own
// period (it follows the page's until someone picks another), sortable, filterable and
// paged. A row opens to the orders that took it, and the checkouts where it went wrong.
//
// The table reads the period's finest rows as soon as it shows (GET /post-purchase/performance/breakdown)
// and sums them here, so picking dimensions on and off is instant from the first click; a new
// period or store reads again. Until they arrive it shows the page's own stats.
//
// Outcomes are two columns: Done (swapped, or added for an add-on) and
// Problems (payment failed, swap failed, errors), each broken down on hover.

type Row = DesignStats & {
  takeRate: number | null;
  done: number;
  problems: number;
  revenuePerView: number | null;
  marginPerView: number | null;
};
type SortKey = keyof Row;

const PAGE_SIZES = [10, 25, 50];

const DIMENSION_LABELS: Record<BreakdownDimension, string> = {
  [BREAKDOWN_DIMENSIONS.DAY]: 'Date',
  [BREAKDOWN_DIMENSIONS.LANDING_PAGE]: 'Landing page',
  [BREAKDOWN_DIMENSIONS.CART]: 'Cart',
  [BREAKDOWN_DIMENSIONS.MATCHED_RULE]: 'Matched rule',
};
const DIMENSION_HINTS: Record<BreakdownDimension, string> = {
  [BREAKDOWN_DIMENSIONS.DAY]: 'The Pacific date of each event.',
  [BREAKDOWN_DIMENSIONS.LANDING_PAGE]: "The order's landing page (its last visit, as Shopify records it), without its query.",
  [BREAKDOWN_DIMENSIONS.CART]: 'What the order held when the offer was shown.',
  [BREAKDOWN_DIMENSIONS.MATCHED_RULE]: "What in the cart brought the offer on, by today's definition of the upsell.",
};
// Picked dimensions show in this order, whatever order they were picked in.
const DIMENSION_ORDER = Object.values(BREAKDOWN_DIMENSIONS);

const COLUMNS: { key: SortKey; label: string; hint?: string }[] = [
  { key: 'offered', label: 'Offered' },
  { key: 'viewed', label: 'Viewed' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'takeRate', label: 'Take rate', hint: 'Accepted ÷ viewed.' },
  { key: 'done', label: 'Done', hint: 'Accepted and completed on the order: swapped, or for an add-on, its products confirmed on it.' },
  { key: 'problems', label: 'Problems', hint: 'Payment failed, swap failed (not since recovered) or an error. Open the row to see each one.' },
  { key: 'revenue', label: 'Revenue', hint: 'The extra charged, before tax.' },
  {
    key: 'margin',
    label: 'Margin',
    hint:
      "Revenue less extra product cost, the card fee and the extra freight: actual for orders that have shipped (their boxes against " +
      'orders with the same items without the upsell), else estimated; before refunds. Hover a value for the all-estimated figure.',
  },
  { key: 'revenuePerView', label: 'Revenue / view' },
  { key: 'marginPerView', label: 'Margin / view' },
];

const OUTCOMES: Record<string, { label: string; color: 'success' | 'error' | 'default' }> = {
  swapped: { label: 'Swapped', color: 'success' },
  added: { label: 'Added', color: 'success' },
  swap_failed: { label: 'Swap failed', color: 'error' },
  accepted: { label: 'Accepted', color: 'default' },
  failed: { label: 'Payment failed', color: 'error' },
  error: { label: 'Error', color: 'error' },
};

// A problem's detail in words: Shopify's message when it sent a list of them.
function detailText(detail: string) {
  try {
    const parsed = JSON.parse(detail);
    if (Array.isArray(parsed)) return parsed.map(entry => entry?.message ?? entry?.code ?? JSON.stringify(entry)).join('; ');
  } catch (error) {
    // Not JSON: shown as it is.
  }
  return detail;
}

const TAKERS_PAGE = 10;

// The orders that took one design, loaded when its row opens.
function Takers({ service, period, store, row }: { service: PostPurchaseService; period: ShownPeriod; store: string; row: Row }) {
  const [result, setResult] = React.useState<{ domain: string; takers: Taker[] } | null | undefined>(undefined);
  const [page, setPage] = React.useState(0);
  const { from, to } = period;
  React.useEffect(() => {
    let current = true;
    // A new period or store: drop the old list while the new one loads.
    setResult(undefined);
    setPage(0);
    service.getTakers(row.upsellId as string, row.designId as string, { from, to }, store).then(response => current && setResult(response));
    return () => {
      current = false;
    };
  }, [service, from, to, store, row.upsellId, row.designId]);
  if (result === undefined) return <LinearProgress aria-label="loading orders that took it" sx={{ my: 1 }} />;
  if (result === null) return <Alert severity="error" sx={{ my: 1 }}>Could not load the orders that took it.</Alert>;
  if (!result.takers.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No order took it, and nothing went wrong, in this period.</Typography>;
  const shop = result.domain.replace('.myshopify.com', '');
  return (
    // Pinned to the left of the wide table, so it stays in view however far it scrolls.
    <Box sx={{ py: 1, position: 'sticky', left: 0, width: 'min(640px, 100%)' }}>
      <Table size="small" aria-label="orders that took it">
        <TableHead>
          <TableRow>
            <TableCell>Order</TableCell>
            <TableCell>When (Pacific)</TableCell>
            <TableCell align="right">Charged</TableCell>
            <TableCell>Outcome</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.takers.slice(page * TAKERS_PAGE, (page + 1) * TAKERS_PAGE).map(taker => (
            <TableRow key={taker.checkoutToken}>
              <TableCell>
                {taker.orderId ? (
                  <a href={`https://admin.shopify.com/store/${shop}/orders/${taker.orderId}`} target="_blank" rel="noreferrer">
                    {taker.orderName ?? taker.orderId}
                  </a>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell>{pacific(taker.at)}</TableCell>
              <TableCell align="right">{taker.charge == null ? '—' : usd(taker.charge)}</TableCell>
              <TableCell>
                <Chip size="small" label={OUTCOMES[taker.outcome]?.label ?? taker.outcome} color={OUTCOMES[taker.outcome]?.color ?? 'default'} />
                {taker.detail && (
                  <Typography variant="caption" color="error" component="div" sx={{ mt: 0.25, maxWidth: 320 }}>
                    {detailText(taker.detail)}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {result.takers.length > TAKERS_PAGE && (
        <TablePagination
          component="div"
          count={result.takers.length}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={TAKERS_PAGE}
          rowsPerPageOptions={[TAKERS_PAGE]}
        />
      )}
    </Box>
  );
}

function PerformanceRow({
  row,
  dimensions,
  current,
  addOn,
  service,
  period,
  store,
}: {
  row: Row;
  dimensions: BreakdownDimension[];
  current: boolean;
  addOn: boolean;
  service: PostPurchaseService;
  period: ShownPeriod;
  store: string;
}) {
  const [open, setOpen] = React.useState(false);
  // Error rows recorded before the request named an offer have no design to open. The orders
  // list can narrow to a day, not to a landing page, cart or rule, so those rows don't open.
  const openable =
    row.upsellId != null &&
    row.designId != null &&
    (row.accepted > 0 || row.problems > 0) &&
    dimensions.every(dimension => dimension === BREAKDOWN_DIMENSIONS.DAY);
  const money = (value: number | null) => (value == null ? '—' : usd(value));
  return (
    <>
      <TableRow
        hover={openable}
        onClick={openable ? () => setOpen(was => !was) : undefined}
        sx={{ cursor: openable ? 'pointer' : 'default', '& > td': { borderBottom: open ? 0 : undefined } }}
      >
        <TableCell padding="checkbox">
          {openable && (
            <IconButton size="small" aria-label="show orders" aria-expanded={open}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        {dimensions.map(dimension =>
          dimension === BREAKDOWN_DIMENSIONS.DAY ? (
            <TableCell key={dimension} sx={{ whiteSpace: 'nowrap' }}>
              {row.day && new Date(`${row.day}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })}
            </TableCell>
          ) : (
            <TableCell key={dimension} sx={{ minWidth: 160, maxWidth: 320, overflowWrap: 'anywhere' }}>
              {row[dimension]}
            </TableCell>
          ),
        )}
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.upsellId ?? '—'}</TableCell>
        <TableCell>
          <Typography variant="body2" fontFamily="monospace" component="span">{row.designId ?? '—'}</Typography>
          {current && <Chip size="small" label="current" sx={{ ml: 1 }} />}
        </TableCell>
        <TableCell align="right">{row.offered}</TableCell>
        <TableCell align="right">{row.viewed}</TableCell>
        <TableCell align="right">{row.accepted}</TableCell>
        <TableCell align="right">{pct(row.accepted, row.viewed)}</TableCell>
        {/* Fewer done than accepted means a customer paid and the change isn't
            on the order yet (or wasn't confirmed): worth a look. */}
        <TableCell align="right" sx={row.done < row.accepted ? { color: 'warning.main', fontWeight: 700 } : undefined}>
          <Tooltip
            title={`${row.done} of ${row.accepted} accepted ${addOn ? 'added to the order' : 'swapped'}${
              row.done < row.accepted ? `; ${row.accepted - row.done} not confirmed yet` : ''
            }`}
          >
            <span>{row.done}</span>
          </Tooltip>
        </TableCell>
        {/* A failed swap a later attempt completed is recovered, not a problem. */}
        <TableCell align="right" sx={row.problems ? { color: 'error.main', fontWeight: 700 } : undefined}>
          <Tooltip
            title={[
              `${row.failed} payment failed`,
              ...(addOn ? [] : [`${row.swap_failed} swap failed`]),
              `${row.errors} errors`,
              ...(row.swap_recovered ? [`${row.swap_recovered} failed swap recovered (nothing to do)`] : []),
            ].join(' · ')}
          >
            <span>{row.problems}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{usd(row.revenue)}</TableCell>
        <TableCell align="right">
          <Tooltip
            title={
              row.marginShippedShare == null
                ? 'Estimated: actual shipping not read.'
                : `${Math.round(row.marginShippedShare * 100)}% of accepts on actual shipping · all estimated: ${money(row.marginEstimate ?? null)}`
            }
          >
            <span>{money(row.margin)}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{money(row.revenuePerView)}</TableCell>
        <TableCell align="right">{money(row.marginPerView)}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{pacific(row.firstSeen)} – {pacific(row.lastSeen)}</TableCell>
      </TableRow>
      {openable && (
        <TableRow>
          <TableCell colSpan={COLUMNS.length + 5 + dimensions.length} sx={{ py: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Takers service={service} period={row.day ? { from: row.day, to: row.day } : period} store={store} row={row} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function PerformanceTable({
  stats: pageStats,
  upsells,
  service,
  period: pageShown,
  pagePeriod,
  store,
}: {
  stats: DesignStats[];
  upsells: Upsell[];
  service: PostPurchaseService;
  period: ShownPeriod;
  pagePeriod: Period;
  store: string;
}) {
  // Its own period, following the page's until someone picks another here; and what to break it down by.
  const [ownPeriod, setOwnPeriod] = React.useState<Period | null>(null);
  const [dimensions, setDimensions] = React.useState<BreakdownDimension[]>([]);
  const [result, setResult] = React.useState<BreakdownResponse | null | undefined>(undefined);
  React.useEffect(() => {
    let current = true;
    setResult(undefined);
    service.getBreakdown(ownPeriod ?? pagePeriod, store).then(response => {
      if (current) setResult(response);
    });
    return () => {
      current = false;
    };
  }, [service, ownPeriod, pagePeriod, store]);
  const ownStats = React.useMemo(() => (result ? sumBreakdown(result.rows, dimensions) : []), [result, dimensions]);
  // The page's stats stand in while the rows load, unless this table has its own period.
  const own = ownPeriod != null || result != null;
  const stats = result ? ownStats : ownPeriod ? [] : pageStats;
  const period = result ? { from: result.from, to: result.to } : pageShown;
  const problems = result ? [result.problem, result.shippingProblem, result.landingPageProblem, result.cartProblem] : [];
  const [upsellFilter, setUpsellFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [currentOnly, setCurrentOnly] = React.useState(false);
  const [sort, setSort] = React.useState<{ key: SortKey; desc: boolean }>({ key: 'revenue', desc: true });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(PAGE_SIZES[0]);

  const pickDimensions = (picked: BreakdownDimension[]) => {
    const next = DIMENSION_ORDER.filter(dimension => picked.includes(dimension));
    setDimensions(next);
    // Newest first when the date is added; back to revenue when the sorted column goes.
    if (next.includes(BREAKDOWN_DIMENSIONS.DAY) && !dimensions.includes(BREAKDOWN_DIMENSIONS.DAY)) setSort({ key: 'day', desc: true });
    else if (dimensions.includes(sort.key as BreakdownDimension) && !next.includes(sort.key as BreakdownDimension)) setSort({ key: 'revenue', desc: true });
    setPage(0);
  };

  const currentDesign = new Map(upsells.map(upsell => [upsell.id, upsell.designId]));
  const isAddOn = (upsellId: string | null) => upsells.find(upsell => upsell.id === upsellId)?.kind === 'add_on';
  const isCurrent = (row: DesignStats) => row.upsellId != null && currentDesign.get(row.upsellId) === row.designId;

  const rows: Row[] = stats.map(row => ({
    ...row,
    takeRate: row.viewed ? row.accepted / row.viewed : null,
    done: row.swapped + row.added,
    problems: row.failed + row.swap_failed + row.errors,
    revenuePerView: row.viewed ? row.revenue / row.viewed : null,
    marginPerView: row.viewed && row.margin != null ? row.margin / row.viewed : null,
  }));
  const needle = search.trim().toLowerCase();
  const filtered = rows.filter(
    row =>
      (upsellFilter === 'all' || row.upsellId === upsellFilter) &&
      (!currentOnly || isCurrent(row)) &&
      (!needle || `${row.upsellId ?? ''} ${row.designId ?? ''}`.toLowerCase().includes(needle)),
  );
  // Unknowns (null) sort last either way.
  const sorted = [...filtered].sort((a, b) => {
    const [x, y] = [a[sort.key], b[sort.key]];
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    const order = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return sort.desc ? -order : order;
  });
  const lastPage = Math.max(0, Math.ceil(sorted.length / pageSize) - 1);
  const shownPage = Math.min(page, lastPage);

  const sortBy = (key: SortKey) => {
    setSort(current => ({ key, desc: current.key === key ? !current.desc : true }));
    setPage(0);
  };
  const header = (key: SortKey, label: string, align: 'left' | 'right', hint?: string) => (
    <TableCell key={key} align={align} sortDirection={sort.key === key ? (sort.desc ? 'desc' : 'asc') : false} sx={{ whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key && !sort.desc ? 'asc' : 'desc'} onClick={() => sortBy(key)}>
        {hint ? (
          <Tooltip title={hint} describeChild>
            <span>{label}</span>
          </Tooltip>
        ) : (
          label
        )}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', px: 2, py: 1.5 }}>
        <Select size="small" value={upsellFilter} onChange={event => { setUpsellFilter(String(event.target.value)); setPage(0); }} inputProps={{ 'aria-label': 'upsell' }}>
          <MenuItem value="all">All upsells</MenuItem>
          {upsells.map(upsell => (
            <MenuItem key={upsell.id} value={upsell.id}>{upsell.id}</MenuItem>
          ))}
        </Select>
        <TextField size="small" placeholder="Search upsell or design" value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} inputProps={{ 'aria-label': 'search designs' }} />
        <FormControlLabel
          control={<Switch size="small" checked={currentOnly} onChange={event => { setCurrentOnly(event.target.checked); setPage(0); }} />}
          label={<Typography variant="body2">Current designs only</Typography>}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" id="performance-breakdown-label">Break down by</Typography>
          <ToggleButtonGroup size="small" value={dimensions} disabled={!result} onChange={(_, picked: BreakdownDimension[]) => pickDimensions(picked)} aria-labelledby="performance-breakdown-label">
            {DIMENSION_ORDER.map(dimension => (
              <ToggleButton key={dimension} value={dimension} sx={{ textTransform: 'none', py: 0.25 }}>
                <Tooltip title={DIMENSION_HINTS[dimension]} describeChild>
                  <span>{DIMENSION_LABELS[dimension]}</span>
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
          <span id="performance-period-label" hidden>Performance period</span>
          <PeriodPicker key={ownPeriod ? 'own' : JSON.stringify(pagePeriod)} period={ownPeriod ?? pagePeriod} onChange={next => { setOwnPeriod(next); setPage(0); }} name="performance-period" labelId="performance-period-label" />
          {ownPeriod && (
            <Typography component="button" variant="caption" onClick={() => setOwnPeriod(null)} sx={{ border: 0, bgcolor: 'transparent', color: 'primary.main', cursor: 'pointer' }}>
              same as page
            </Typography>
          )}
        </Box>
      </Box>
      {result === undefined && <LinearProgress aria-label="loading performance" />}
      {result === null && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>Could not load performance for this period.</Alert>}
      {own && problems.map(problem => problem && <Alert key={problem} severity="warning" sx={{ mx: 2, mb: 1 }}>{problem}</Alert>)}
      {result && dimensions.some(dimension => dimension === BREAKDOWN_DIMENSIONS.LANDING_PAGE || dimension === BREAKDOWN_DIMENSIONS.CART) && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pb: 1 }}>
          Of {result.coverage.viewedCheckouts} checkouts that saw the page, {result.coverage.withLandingPage} have a known landing page and{' '}
          {result.coverage.withCart} a known cart. Per upsell, landing pages beyond the {result.topValues.landingPage} most viewed and carts beyond the{' '}
          {result.topValues.cart} most viewed read Other. Rows broken down by landing page, cart or rule don&apos;t open to their orders.
        </Typography>
      )}
      {own && !result ? null : !sorted.length ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {stats.length ? 'No design matches these filters.' : 'No upsell activity in this period.'}
        </Typography>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="upsell performance">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {dimensions.map(dimension => header(dimension, DIMENSION_LABELS[dimension], 'left', DIMENSION_HINTS[dimension]))}
                  {header('upsellId', 'Upsell', 'left')}
                  {header('designId', 'Design', 'left')}
                  {COLUMNS.map(column => header(column.key, column.label, 'right', column.hint))}
                  {header('lastSeen', 'Seen (Pacific)', 'left')}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.slice(shownPage * pageSize, (shownPage + 1) * pageSize).map(row => (
                  <PerformanceRow
                    key={JSON.stringify([row.upsellId, row.designId, ...dimensions.map(dimension => row[dimension])])}
                    row={row}
                    dimensions={dimensions}
                    current={isCurrent(row)}
                    addOn={isAddOn(row.upsellId)}
                    service={service}
                    period={period}
                    store={store}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={sorted.length}
            page={shownPage}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZES}
            onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setPage(0); }}
          />
        </>
      )}
    </>
  );
}
